import React, { useState, useEffect, useRef } from 'react';
import './AddressAutocomplete.css';

interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect: (place: any) => void;
  placeholder: string;
  label?: string;
  autocompleteService: any;
  placesService: any;
  error?: string;
  hasValidLocation?: boolean;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder,
  label,
  autocompleteService,
  placesService,
  error,
  hasValidLocation = false,
}) => {
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSuggestions = (input: string) => {
    if (!autocompleteService || !input || input.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    autocompleteService.getPlacePredictions(
      {
        input: input,
        types: ['establishment', 'geocode'],
        componentRestrictions: undefined,
      },
      (predictions: PlacePrediction[] | null, status: string) => {
        if (status === window.google?.maps?.places?.PlacesServiceStatus?.OK && predictions) {
          setSuggestions(predictions);
          setShowSuggestions(true);
          setSelectedIndex(-1);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    getSuggestions(newValue);
  };

  const handleSelectPlace = (prediction: PlacePrediction) => {
    onChange(prediction.description);
    setShowSuggestions(false);
    setSuggestions([]);

    if (placesService && prediction.place_id) {
      placesService.getDetails(
        {
          placeId: prediction.place_id,
          fields: [
            'formatted_address',
            'address_components',
            'geometry',
            'name',
            'place_id',
          ],
        },
        (place: any, status: string) => {
          if (status === window.google?.maps?.places?.PlacesServiceStatus?.OK && place) {
            onPlaceSelect(place);
          }
        }
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectPlace(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  return (
    <div className="address-autocomplete" ref={containerRef}>
      <div className="location position-relative">
        {label && (<div className="d-flex justify-content-between align-items-center">
          <label className="form-label">{label}</label>
        </div>)}
        <div className="pickup position-relative">
          <input
            ref={inputRef}
            type="text"
            className={`form-control ${error ? 'error' : ''}`}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => value.length >= 2 && suggestions.length > 0 && setShowSuggestions(true)}
            placeholder={placeholder}
          />
          <svg
            className="search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
              stroke="#9D9D9D"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {showSuggestions && suggestions.length > 0 && (
            <div className="suggestions-dropdown">
              {suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.place_id}
                  className={`suggestion-item ${
                    index === selectedIndex ? 'selected' : ''
                  }`}
                  onClick={() => handleSelectPlace(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="suggestion-main">
                    {suggestion.structured_formatting?.main_text || suggestion.description.split(',')[0]}
                  </div>
                  <div className="suggestion-secondary">
                    {suggestion.structured_formatting?.secondary_text || 
                     suggestion.description.split(',').slice(1).join(',').trim()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {error && (
          <div className="address-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressAutocomplete;



